import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCasinoGameDetails, getMatchExposure, getCasinoOpenBets } from "../../../apiservices/CasionApi";
import ViewMoreModal from "../../../components/ViewMoreModal";
import { extractCasinoGame } from "../../../utils/casinoGame";
import { formatBetNation, formatBetOdds, formatWorliMyBetsOdds } from "../../../utils/casinoMyBets";
import RefPlayerCardsOverlay from "../../casinoComponents/videoBox/overlays/RefPlayerCardsOverlay";
import WorliResults from "./WorliResults";
import MatkaTabs from "./MatkaTabs";
import MatkaVideo from "./MatkaVideo";
import "../../../styles/games/worli.css";

const TITLES = { worli: "Worli Matka", worli2: "Instant Worli", worli3: "Matka" };
const WORLI_CARD_GROUPS = [{ title: "Cards", idx: [0, 1, 2] }];

export default function WorliGame() {
  const [gameData, setGameData] = useState(null);
  const [, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname);
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;

  useEffect(() => {
    const gameName = extractCasinoGame(pathname);

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        setGameData(response);
      } catch (err) {
        console.error("API Fetch Error:", err.message);
        setGameData(null); // clear data on error
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const matchId = gameData?.data?.data?.mid?.toString();

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response.success && response.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const teamName = item.team_name || item.selection || item.player_name || "";
            const exposureAmount = parseFloat(item.exposure_amount) || 0;
            if (teamName && exposureAmount !== 0) {
              exposureMap[teamName] = exposureAmount;
              exposureMap[teamName.toLowerCase()] = exposureAmount;
              exposureMap[teamName.trim()] = exposureAmount;
            }
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error('Error fetching exposure:', error);
    }
  }, [matchId]);

  // My Bets: same API as lord-admin – GET /lords/bet-list/casino/open-downline?gameName=...
  const fetchMyBetsData = useCallback(async () => {
    const gameName = extractCasinoGame(pathname);
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [pathname]);

  // Fetch exposure and My Bets on mount and periodically (My Bets every 5s like lord-admin)
  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => {
        fetchExposure();
      }, 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    const t = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(t);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  const title = TITLES[gameName] || gameName;
  // tenexch WorliMatka / InstantWorli / Worli3 My Bets: reference names ("ALL SP", "35 Jodi"),
  // Line / Odd / Even at the board rate; Worli Matka prints 0 for a Cycle bet.
  const formatNation = (nat, bet) => formatBetNation(gameName, nat, bet);
  const formatRate = (bet) => {
    const rate = formatBetOdds(gameName, bet, bet.odds ?? bet.userrate);
    return gameName === "worli" ? formatWorliMyBetsOdds(bet, rate) : rate;
  };
  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
      formatNation={formatNation}
      formatRate={formatRate}
    />
  );
  const rightSide = <MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} formatNation={formatNation} formatRate={formatRate} />;

  // Matka (worli3): the feed is the market list — reference .casino-table.worli.detail-page-container.matka:
  // market tabs, empty .game-header, video (no cards / timer), Last Result (.matka-result). No bet table on admin.
  if (gameName === "worli3") {
    const markets = Array.isArray(gameData?.data?.data) ? gameData.data.data : [];
    const activeIndex = activeTabIndex < markets.length ? activeTabIndex : 0;
    return (
      <CasinoTableLayout tableClass="worli detail-page-container matka" rightSide={rightSide}>
        <>
          <MatkaTabs markets={markets} activeIndex={activeIndex} onSelect={(i) => setActiveTabIndex(i)} />{" "}
          <div className="game-header" />{" "}
          <MatkaVideo iframeSrc={iframeSrc} rulesType={gameName} />{" "}
          <div className="last-result-title">
            <h5>Last Result</h5>{" "}
            <Link to={`/admin/reports/casinoresult/${gameName}`} className="result-more">
              {"\n              View All\n            "}
            </Link>
          </div>{" "}
          <div className="casino-video-last-results matka-result">
            <WorliResults gameType={gameName} fallbackTitle={title} />
          </div>
        </>
        {viewMoreModal}
      </CasinoTableLayout>
    );
  }

  // Worli Matka / Instant Worli: reference .casino-table.worli.detail-page-container —
  // heading, video (3-card overlay + timer), Last Result ("R" items). No bet table on admin.
  const d = gameData?.data?.data;
  return (
    <CasinoTableLayout tableClass="worli detail-page-container" rightSide={rightSide}>
      <>
        <CasinoHeading name={title} roundId={d?.mid || ""} />
        <VideoBox
          iframeSrc={iframeSrc}
          timerValue={d?.lt}
          gameType={gameName}
          cardsOverlay={<RefPlayerCardsOverlay card={d?.card} groups={WORLI_CARD_GROUPS} />}
        />
        <CasinoLastResult
          gameType={gameName}
          resultComponent={<WorliResults gameType={gameName} fallbackTitle={title} />}
        />
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
